import type { i18n as I18nInstance } from "i18next";

import { LANGUAGE_CODE } from "consts";

type TranslationBundle = Record<string, unknown>;

type BundledTranslationFallbackParams = {
  i18nInstance: Pick<I18nInstance, "addResourceBundle" | "hasLoadedNamespace">;
  // resolves null when the file does not exist, rejects when it could not be loaded (the next missing key tries again)
  loadBundle: (url: string) => Promise<TranslationBundle | null>;
  translationsBaseUrl: string;
  toFileLanguageCode: (languageCode: string) => string;
};

const IN_CONTEXT_LANGUAGE_PREFIX = "ach";
const RETRY_AFTER_FAILURE_MS = 30 * 1000;

// When a key is missing in the Crowdin texts (for example after a rollback), the texts shipped with the same build are loaded and added next to the Crowdin texts.
// Crowdin texts always win: only missing keys are added. The shipped zu texts are added to the fallback language as the last resort, so users never see key names.
export const createBundledTranslationFallback = ({ i18nInstance, loadBundle, translationsBaseUrl, toFileLanguageCode }: BundledTranslationFallbackParams) => {
  const requestedBundles = new Map<string, Promise<void>>();

  const bundleUrl = (languageCode: string, namespace: string): string =>
    `${translationsBaseUrl}/translations/${toFileLanguageCode(languageCode)}/${namespace}.json`;

  const addMissingKeys = async (languageCode: string, storeLanguageCode: string, namespace: string): Promise<void> => {
    const bundle = await loadBundle(bundleUrl(languageCode, namespace));
    if (bundle) i18nInstance.addResourceBundle(storeLanguageCode, namespace, bundle, true, false);
  };

  return (languages: string | readonly string[], namespace: string): Promise<void> => {
    // A namespace that is not loaded yet must stay untouched: adding a bundle here would make i18next treat it as loaded and skip loading it from Crowdin.
    if (!i18nInstance.hasLoadedNamespace(namespace)) return Promise.resolve();

    const languageCodes = (Array.isArray(languages) ? languages : [languages]).filter(
      (languageCode: string) => languageCode !== LANGUAGE_CODE.ZULU && !languageCode.startsWith(IN_CONTEXT_LANGUAGE_PREFIX)
    );
    const requestId = `${languageCodes.join(",")}/${namespace}`;
    const pendingRequest = requestedBundles.get(requestId);
    if (pendingRequest) return pendingRequest;

    const request = Promise.all(languageCodes.map((languageCode) => addMissingKeys(languageCode, languageCode, namespace)))
      .then(() => addMissingKeys(LANGUAGE_CODE.ZULU, LANGUAGE_CODE.GERMAN, namespace))
      .catch(() => {
        // a failed load is tried again later, not on every render
        setTimeout(() => requestedBundles.delete(requestId), RETRY_AFTER_FAILURE_MS);
      });
    requestedBundles.set(requestId, request);
    return request;
  };
};
